import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Form, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import { getDocumentTypes, toggleDocumentTypeStatus } from 'helpers/documentManagerApi';
import ManageTypeGroupsModal from './ManageTypeGroupsModal';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';

const DocumentTypeList = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [selectedTypeForGroups, setSelectedTypeForGroups] = useState(null);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const res = await getDocumentTypes(true);
      if (res && res.status) {
        setTypes(res.data || []);
      } else {
        toast.error(res?.message || 'Failed to fetch document types');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error loading document types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleToggleStatus = async (typeId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const res = await toggleDocumentTypeStatus(typeId, newStatus);
      if (res && res.status) {
        toast.success(`Document type ${newStatus ? 'activated' : 'deactivated'} successfully`);
        // Update local state
        setTypes(prev => prev.map(t => t.id === typeId ? { ...t, is_active: newStatus } : t));
      } else {
        toast.error(res?.message || 'Failed to toggle status');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error toggling document type status');
    }
  };

  const handleManageGroupsClick = (type) => {
    setSelectedTypeForGroups(type);
    setShowGroupsModal(true);
  };

  const handleEditClick = (id) => {
    navigate(`/main/admin/document-types/${id}`);
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Type Name',
      meta: { headerProps: { className: 'pe-1 text-900' } }
    },
    {
      accessorKey: 'file_mode',
      header: 'File Mode',
      meta: { headerProps: { className: 'pe-1 text-900' } },
      cell: ({ row: { original } }) => (
        <span className="text-capitalize">{original.file_mode}</span>
      )
    },
    {
      accessorKey: 'fields_count',
      header: 'Fields',
      meta: { headerProps: { className: 'pe-1 text-900 text-center' }, cellProps: { className: 'text-center' } },
      cell: ({ row: { original } }) => (original.fields ? original.fields.length : 0)
    },
    {
      accessorKey: 'optout_count',
      header: 'Groups Opted-Out',
      meta: { headerProps: { className: 'pe-1 text-900 text-center' }, cellProps: { className: 'text-center' } },
      cell: ({ row: { original } }) => original.opted_out_group_count || 0
    },
    {
      accessorKey: 'is_active',
      header: 'Active',
      meta: { headerProps: { className: 'pe-1 text-900 text-center' }, cellProps: { className: 'text-center' } },
      cell: ({ row: { original } }) => (
        <Form.Check
          type="switch"
          id={`active-switch-${original.id}`}
          checked={!!original.is_active}
          onChange={() => handleToggleStatus(original.id, original.is_active)}
          className="d-inline-block"
        />
      )
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } },
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <div className="d-flex justify-content-center gap-2">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-primary"
            onClick={() => handleEditClick(original.id)}
            title="Edit Type"
          >
            <FontAwesomeIcon icon="edit" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-secondary"
            onClick={() => handleManageGroupsClick(original)}
            title="Manage Groups"
          >
            <FontAwesomeIcon icon="cog" />
          </Button>
        </div>
      )
    }
  ];

  const table = useAdvanceTable({
    data: types,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Document-types', href: '', isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <Card className="mb-3">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
          <h5 className="mb-0">Document Types</h5>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/main/admin/document-types/create')}
          >
            <FontAwesomeIcon icon="plus" className="me-2" />
            Add Type
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4">
              <ShimmerUI />
            </div>
          ) : (
            <AdvanceTableProvider {...table}>
              <Row className="p-3 align-items-center">
                <Col xs="auto" className="ms-auto">
                  <AdvanceTableSearchBox placeholder="Search types..." />
                </Col>
              </Row>
              <div className='p-3'>
                <AdvanceTable
                  headerClassName="bg-200 text-900 align-middle"
                  rowClassName="align-middle"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0'
                  }}
                />
              </div>
              <div className="p-3">
                <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
              </div>
            </AdvanceTableProvider>
          )}
        </Card.Body>

        {showGroupsModal && (
          <ManageTypeGroupsModal
            show={showGroupsModal}
            handleClose={() => {
              setShowGroupsModal(false);
              setSelectedTypeForGroups(null);
              fetchTypes(); // Reload count of opt-outs
            }}
            type={selectedTypeForGroups}
          />
        )}
      </Card>
    </>
  );
};

export default DocumentTypeList;
